import { useEffect, useState } from 'react';
import { ExternalLink, Loader2, Save, Trash2, ShieldCheck, KeyRound } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import {
  usePlatformExplorers,
  useSaveExplorerKey,
  useRemoveExplorerKey,
  type ExplorerProviderId,
  type ExplorerProviderInfo,
  type ExplorerStatus,
} from '@/hooks/usePlatform';
import { ApiError } from '@/lib/api-client';
import { formatDate } from '@/lib/utils';
import { useConfirm } from '@/components/ConfirmDialog';

export function PlatformExplorersPage() {
  const { data, isPending } = usePlatformExplorers();

  return (
    <div className="max-w-3xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <ShieldCheck className="w-5 h-5" /> Blockchain Explorer'lar
        </h1>
        <p className="text-muted-foreground text-sm">
          Kripto sepetlerinin TXID'lerini explorer API'lerinden otomatik doğrulamak için. Etherscan tek key ile 7 EVM ağı açar.
        </p>
      </div>

      {isPending ? (
        <div className="text-muted-foreground">Yükleniyor…</div>
      ) : (
        <div className="space-y-3">
          {data?.providers.map((p) => {
            const status = data.statuses.find((s) => s.provider === p.id);
            return <ProviderCard key={p.id} provider={p} status={status} />;
          })}
        </div>
      )}

      <Card className="bg-muted/30">
        <CardContent className="p-4 text-xs text-muted-foreground space-y-1">
          <p className="font-medium text-foreground">Otomatik doğrulama nasıl çalışır?</p>
          <p>
            Her dakika çalışan cron, <code>pending_verification</code> durumundaki sepetlerde kullanıcının gönderdiği TXID'yi ilgili ağın explorer API'sine sorar. Doğru cüzdan + doğru token + doğru miktar + yeterli confirmation varsa sepet otomatik onaylanır ve kalemler işlenir.
          </p>
          <p>
            Doğrulanamayan / red edilen sepetler Sepet Onay Kuyruğu'nda kalır; admin manuel onay/red edebilir.
          </p>
        </CardContent>
      </Card>
    </div>
  );
}

function ProviderCard({
  provider,
  status,
}: {
  provider: ExplorerProviderInfo;
  status?: ExplorerStatus;
}) {
  const save = useSaveExplorerKey();
  const remove = useRemoveExplorerKey();
  const confirm = useConfirm();
  const [key, setKey] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (saved) {
      const t = setTimeout(() => setSaved(false), 2000);
      return () => clearTimeout(t);
    }
  }, [saved]);

  const onSave = async () => {
    setError(null);
    try {
      await save.mutateAsync({ provider: provider.id as ExplorerProviderId, key });
      setKey('');
      setSaved(true);
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Kaydedilemedi');
    }
  };

  const onRemove = async () => {
    const ok = await confirm({
      title: `${provider.label} key'i sil?`,
      description: 'Key silinirse bu ağdaki sepetler otomatik doğrulanamaz.',
      danger: true,
    });
    if (!ok) return;
    await remove.mutateAsync(provider.id as ExplorerProviderId);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            <div className="w-9 h-9 rounded-lg bg-primary/10 text-primary flex items-center justify-center shrink-0">
              <KeyRound className="w-4 h-4" />
            </div>
            <div className="min-w-0 flex-1">
              <CardTitle className="text-base flex items-center gap-2">
                {provider.label}
                {provider.required && (
                  <span className="inline-flex items-center rounded-full bg-primary/10 text-primary text-[10px] px-1.5 py-0.5 font-medium">
                    ÖNERİLEN
                  </span>
                )}
                {status?.configured && (
                  <span className="inline-flex items-center rounded-full bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 text-[10px] px-1.5 py-0.5 font-medium">
                    AKTİF
                  </span>
                )}
              </CardTitle>
              <CardDescription className="text-xs mt-0.5">{provider.description}</CardDescription>
            </div>
          </div>
          <a
            href={provider.docs_url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-primary hover:underline inline-flex items-center gap-1 shrink-0"
          >
            Key al <ExternalLink className="w-3 h-3" />
          </a>
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {status?.configured && (
          <div className="rounded-md border bg-muted/30 p-3 text-xs flex items-center justify-between gap-2">
            <div>
              Mevcut key: <code className="font-mono">{status.key_hint}</code>
              {status.updated_at && (
                <span className="text-muted-foreground ml-2">· {formatDate(status.updated_at)}</span>
              )}
            </div>
            <Button size="sm" variant="ghost" onClick={onRemove} disabled={remove.isPending}>
              <Trash2 className="w-3.5 h-3.5" />
              Sil
            </Button>
          </div>
        )}
        <div className="space-y-2">
          <Label htmlFor={`key-${provider.id}`}>
            {status?.configured ? 'Key güncelle' : 'API Key'}
          </Label>
          <div className="flex gap-2">
            <Input
              id={`key-${provider.id}`}
              type="password"
              value={key}
              onChange={(e) => setKey(e.target.value)}
              placeholder="API key'i buraya yapıştır"
              className="font-mono text-xs"
            />
            <Button size="sm" onClick={onSave} disabled={save.isPending || key.length < 4}>
              {save.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              Kaydet
            </Button>
          </div>
          {error && (
            <div className="text-xs text-destructive bg-destructive/10 rounded px-2 py-1.5">{error}</div>
          )}
          {saved && (
            <div className="text-xs text-emerald-700 dark:text-emerald-400 bg-emerald-500/10 rounded px-2 py-1.5">
              Kaydedildi ✓
            </div>
          )}
        </div>
      </CardContent>
    </Card>
  );
}
